import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TFunction } from 'i18next'
import { Box, Chip, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import {
  BackgroundBoxHeritageContainer,
  SessionItem,
  Spinner,
  UpcomingSessionData,
  Button
} from '../../../interaxDesignSystemComponents'
import { useInfiniteUpcomingActivitiesForGamer } from '../../../apiLayer/queryHooks/queries'
import { INTERAX_COLORS } from '../../../constants/colors'
import { PageHeader } from '../../../shared/components/PageHeader'
import { ActivityWithSessions } from '../../../types/GeneralTypes'
import { PageWrapper } from '../../../shared/components'
import { shouldShowGoDashboardButton } from '../helpers/activityHelpers'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { ActivityType } from '../../../../../pure-js/enums/ActivityType'
import { isAIDebriefActivity } from '../../../../../pure-js/libs/isAIDebriefActivity'
import { activityUsesSprintRoom } from '../../../../../pure-js/libs/activityUsesDedicatedRoom'
import { buildSprintCallPath } from '../../../config/routes/routeConfig'
import { GamerUpcomingActivityDetailsPanel } from '../components'
import { GameDesktopOnlyModal } from '../components/GameDesktopOnlyModal'
import { useMobileGameGuard } from '../hooks/useMobileGameGuard'

const GAME_TYPES = [ActivityType.FIRST_GAME, ActivityType.SHORT_GAME, ActivityType.LONG_GAME] as string[]
const REFLECTION_TYPES_ALL = [ActivityType.SHORT_REFLECTION, ActivityType.LONG_REFLECTION] as string[]

const getActivityStatus = (activity: ActivityWithSessions): 'live' | 'upcoming' | 'completed' => {
  if (activity.endedAt) return 'completed'
  if (activity.startDate && activity.endDate) {
    const now = new Date()
    const start = new Date(activity.startDate)
    const end = new Date(activity.endDate)
    if (now >= start && now <= end) return 'live'
  }
  return 'upcoming'
}

const resolveActivityButtonText = (
  t: TFunction,
  { isJoinable, showGoDashboard }: { isJoinable: boolean; showGoDashboard: boolean }
): string => {
  if (isJoinable) return t('gamer.upcomingActivities.joinNowButton')
  if (showGoDashboard) return t('gamer.upcomingActivities.goDashboardButton')
  return t('gamer.upcomingActivities.viewDetailsButton')
}

const filterSelectSx = {
  color: INTERAX_COLORS.white,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: INTERAX_COLORS.sun
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: INTERAX_COLORS.sun
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: INTERAX_COLORS.sun
  },
  '& .MuiSvgIcon-root': {
    color: INTERAX_COLORS.sun
  }
}

export const AllUpcomingActivitiesPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')

  const {
    data,
    isLoading: loading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteUpcomingActivitiesForGamer()

  const upcomingActivities = useMemo<ActivityWithSessions[]>(
    () => data?.pages.flatMap((page) => page.data?.activities ?? []) ?? [],
    [data]
  )
  const totalActivities = data?.pages[0]?.data?.total ?? upcomingActivities.length

  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = loadMoreRef.current
    if (!node || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const filteredActivities = useMemo(() => {
    let filtered: ActivityWithSessions[] = [...upcomingActivities]
    if (statusFilter !== 'all') {
      filtered = filtered.filter((activity) => getActivityStatus(activity) === statusFilter)
    }
    if (typeFilter === 'game') {
      filtered = filtered.filter((a) => GAME_TYPES.includes(a.type ?? ''))
    } else if (typeFilter === 'reflection') {
      filtered = filtered.filter((a) => REFLECTION_TYPES_ALL.includes(a.type ?? ''))
    }
    return filtered
  }, [upcomingActivities, statusFilter, typeFilter])

  const statusCounts = useMemo(() => {
    const liveCount = upcomingActivities.filter((a) => getActivityStatus(a) === 'live').length
    const upcomingCount = upcomingActivities.filter((a) => getActivityStatus(a) === 'upcoming').length
    return { liveCount, upcomingCount }
  }, [upcomingActivities])

  const { guardGameEntry, isDesktopOnlyModalOpen, closeDesktopOnlyModal } = useMobileGameGuard()

  const handleJoinActivity = useCallback(
    (activityId: string) => {
      const activity = upcomingActivities.find((a) => a.id === activityId)
      const isSprintCall = !!activity && activityUsesSprintRoom(activity.type)

      if (isAIDebriefActivity(activity?.type)) {
        navigate(GamerRoutes.AI_COACH)
      } else if (isSprintCall) {
        navigate(buildSprintCallPath({ sprintId: activity.sprintId, activityId: activity.id }))
      } else {
        // Game activities are desktop-only — blocked on phones/tablets (the AI debrief above stays allowed).
        guardGameEntry(() => {
          navigate(`/gamer/play-game-without-introduction`)
        })
      }
    },
    [navigate, upcomingActivities, guardGameEntry]
  )

  const handleViewDetails = useCallback(
    (activityId: string) => {
      navigate(`/gamer/activity-details?id=${activityId}`)
    },
    [navigate]
  )

  const handleBack = useCallback(() => {
    navigate(GamerRoutes.UPCOMING_ACTIVITIES)
  }, [navigate])

  if (loading) {
    return (
      <PageWrapper sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper className="all-upcoming-activities-page">
      <PageHeader
        title={t('gamer.upcomingActivities.allActivitiesTitle')}
        subtitle={t('gamer.upcomingActivities.allActivitiesSubtitle')}
        actions={
          <Button variant="secondary" onClick={handleBack} icon={<ArrowLeftIcon />}>
            {t('gamer.upcomingActivities.backToActivityCenter')}
          </Button>
        }
      />

      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <BackgroundBoxHeritageContainer>
          {/* Stats and Filters */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Chip
                label={t('gamer.upcomingActivities.liveSessionsCount', { count: statusCounts.liveCount })}
                sx={{
                  backgroundColor: `${INTERAX_COLORS.green}40`,
                  color: INTERAX_COLORS.green,
                  fontWeight: 600
                }}
              />
              <Chip
                label={t('gamer.upcomingActivities.upcomingCount', { count: statusCounts.upcomingCount })}
                sx={{
                  backgroundColor: INTERAX_COLORS.sun40,
                  color: INTERAX_COLORS.sun,
                  fontWeight: 600
                }}
              />
              <Chip
                label={t('gamer.upcomingActivities.totalActivitiesCount', { count: totalActivities })}
                sx={{
                  backgroundColor: `${INTERAX_COLORS.white}20`,
                  color: INTERAX_COLORS.white,
                  fontWeight: 600,
                  border: `1px solid ${INTERAX_COLORS.white}40`
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: INTERAX_COLORS.white }}>
                  {t('gamer.upcomingActivities.statusFilter')}
                </InputLabel>
                <Select
                  value={statusFilter}
                  label={t('gamer.upcomingActivities.statusFilter')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={filterSelectSx}
                >
                  <MenuItem value="all">{t('gamer.upcomingActivities.allStatus')}</MenuItem>
                  <MenuItem value="live">{t('gamer.upcomingActivities.liveStatus')}</MenuItem>
                  <MenuItem value="upcoming">{t('gamer.upcomingActivities.upcomingStatus')}</MenuItem>
                  <MenuItem value="completed">{t('gamer.upcomingActivities.completedStatus')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: INTERAX_COLORS.white }}>{t('gamer.upcomingActivities.typeFilter')}</InputLabel>
                <Select
                  value={typeFilter}
                  label={t('gamer.upcomingActivities.typeFilter')}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  sx={filterSelectSx}
                >
                  <MenuItem value="all">{t('gamer.upcomingActivities.allTypes')}</MenuItem>
                  <MenuItem value="game">{t('gamer.upcomingActivities.gamingSessions')}</MenuItem>
                  <MenuItem value="reflection">{t('gamer.upcomingActivities.reflectionActivities')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Activities List */}
          {filteredActivities.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredActivities.map((activity) => {
                const activityDate = activity.startDate ? new Date(activity.startDate) : null
                const activityStatus = getActivityStatus(activity)

                if (!activityDate || isNaN(activityDate.getTime())) {
                  return null
                }

                const details = <GamerUpcomingActivityDetailsPanel activity={activity} />
                const showGoDashboard = shouldShowGoDashboardButton(activity)
                const isJoinable = activityStatus === 'live'
                const buttonText = resolveActivityButtonText(t, { isJoinable, showGoDashboard })

                const handleButtonClick = () => {
                  if (isJoinable) {
                    handleJoinActivity(activity.id)
                  } else if (showGoDashboard) {
                    navigate(GamerRoutes.DASHBOARD)
                  } else {
                    handleViewDetails(activity.id)
                  }
                }

                const upcomingData: UpcomingSessionData = {
                  day: activityDate.getDate(),
                  month: activityDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
                  title: activity.title || t('gamer.upcomingActivities.untitledActivity'),
                  sprintName: activity.sprintTitle,
                  teams: activity.teamIds?.length || 0,
                  participants: activity.participants?.length || 0,
                  time: activity.startDate
                    ? new Date(activity.startDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                    : '',
                  status: activityStatus,
                  buttonText,
                  onButtonClick: handleButtonClick,
                  expandable: true,
                  details
                }

                return <SessionItem key={activity.id} type="upcoming" upcomingData={upcomingData} />
              })}
            </Box>
          ) : (
            <Box sx={{ p: 6, textAlign: 'center', color: INTERAX_COLORS.typographyFont }}>
              {upcomingActivities.length === 0
                ? t('gamer.upcomingActivities.noActivities')
                : t('gamer.upcomingActivities.noMatchingFilters')}
            </Box>
          )}

          {(hasNextPage || isFetchingNextPage) && (
            <Box
              ref={loadMoreRef}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pt: 4 }}
            >
              <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 13 }}>
                {t('gamer.upcomingActivities.showingCount', {
                  shown: upcomingActivities.length,
                  total: totalActivities
                })}
              </Typography>
              {isFetchingNextPage ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Spinner />
                  <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 14 }}>
                    {t('gamer.upcomingActivities.loadingMore')}
                  </Typography>
                </Box>
              ) : (
                <Button variant="secondary" onClick={() => fetchNextPage()}>
                  {t('gamer.upcomingActivities.loadMore')}
                </Button>
              )}
            </Box>
          )}
        </BackgroundBoxHeritageContainer>
      </Box>

      <GameDesktopOnlyModal isOpen={isDesktopOnlyModalOpen} onClose={closeDesktopOnlyModal} />
    </PageWrapper>
  )
}
